/**
 * What the browser knows about its own orders — ROADMAP 4.9/4.13.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A VIEW, NOT A RECORD
 *
 * The authoritative state of every order is in Postgres, and the journal
 * beside it says how it got there. This store holds what the screen is
 * currently showing, so a refusal and a fill appear without a reload.
 *
 * Nothing here is ever the basis for deciding whether to send something.
 * That decision is the engine's, behind the gates, against the broker.
 *
 * WHY NOT ZUSTAND, WHICH THIS APP ALREADY HAS
 *
 * It could be. The feed's `useSyncExternalStore` pattern is used instead for
 * consistency with `tickStore` and because the update shape is identical —
 * an external source pushes, components subscribe to their own slice. The
 * volume here is a handful of events a minute rather than 15 Hz, so the
 * performance argument that drove the feed does not apply; the consistency
 * one does.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { ExecutionEvent, OrderState, RmsGate } from '@apna/shared'

/** Which account an order went to. */
export interface OrderBroker {
  connectionId: string
  label: string
  mode: string
}

/** One order as the UI shows it. */
export interface TrackedIntent {
  premiumLimit?: number | null
  premiumWaiting?: boolean
  clientRequestId: string
  intentId: string | null
  state: OrderState | 'SUBMITTING'
  refusedGate: RmsGate | null
  /** The sentence shown to the user, verbatim from the gate or the broker. */
  message: string | null
  legs: ExecutionEvent['legs']
  /** For sorting. Unix ms. */
  at: number
  /** What the user asked for, so the row reads correctly before any delta. */
  label: string
  /**
   * The basket this order was placed for — a strategy's name, or a chain
   * combo's own label. Null for a loose chain click.
   *
   * Same lifecycle as `broker`: not on the wire from the engine, known at
   * submit time or supplied by the next hydrate. An executed row uses it to
   * say what the order was FOR, which the leg alone cannot.
   */
  groupName?: string | null
  /**
   * What the order was trying to DO — ENTRY, EXIT, SQUARE_OFF.
   *
   * Same lifecycle as `groupName`: supplied on hydrate, absent on a live
   * engine event. Absent reads as an entry, which is what the overwhelming
   * majority of unlabelled rows are.
   */
  kind?: string | null
  /**
   * WHICH ACCOUNT. Null until it is known.
   *
   * Not on the wire. `ExecutionEvent` carries no connection — it is a delta
   * about an order, and adding a field to the engine's event for a column
   * would be changing a protocol to label a table. The browser knows it at
   * submit time and the server supplies it on hydrate, which between them
   * cover every row: a live event for an order placed elsewhere arrives
   * unlabelled and is filled in by the next hydrate.
   */
  broker: OrderBroker | null
}

type Listener = () => void

const MAX_TRACKED = 200

class OrderStore {
  private order: string[] = []
  private byRequest = new Map<string, TrackedIntent>()
  private touchedAtVersion = new Map<string, number>()
  private readonly listeners = new Set<Listener>()
  /** Bumped on every change; `useSyncExternalStore` compares it. */
  private version = 0
  private snapshot: TrackedIntent[] = []

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /**
   * Newest first.
   *
   * Returns a CACHED array, rebuilt only when something changed.
   * `useSyncExternalStore` compares snapshots by identity and would loop
   * forever on a fresh array every call.
   */
  getSnapshot = (): TrackedIntent[] => this.snapshot

  getVersion = (): number => this.version

  /**
   * Load today's orders from the server.
   *
   * MERGES rather than replaces: a row this tab is mid-flight on (still
   * SUBMITTING, no intent id yet) must not be dropped by a hydrate that
   * raced it, and a live event that already arrived must not be rolled
   * back to the server's older snapshot.
   *
   * Keyed on `clientRequestId`, which is the same key live events use —
   * hydrating on anything else would show every order twice the moment the
   * first event landed.
   */
  hydrate(rows: readonly TrackedIntent[], versionAtRead?: number): void {
    let updated = false
    for (const row of rows) {
      const existing = this.byRequest.get(row.clientRequestId)
      // With a request watermark, only writes DURING that request outrank
      // its result. History timestamps are creation times, not revisions.
      if (existing && (versionAtRead === undefined
        ? existing.at >= row.at && existing.state !== 'SUBMITTING'
        : (this.touchedAtVersion.get(row.clientRequestId) ?? 0) > versionAtRead)) continue
      if (!this.byRequest.has(row.clientRequestId)) this.order.push(row.clientRequestId)
      this.byRequest.set(
        row.clientRequestId,
        existing
          ? {
              ...row,
              label: existing.label,
              broker: row.broker ?? existing.broker,
              groupName: row.groupName ?? existing.groupName,
              kind: row.kind ?? existing.kind,
            }
          : row,
      )
      this.touchedAtVersion.set(row.clientRequestId, this.version + 1)
      updated = true
    }
    if (!updated) return
    // Newest first, by the server's own timestamps.
    this.order.sort((a, b) => (this.byRequest.get(b)?.at ?? 0) - (this.byRequest.get(a)?.at ?? 0))
    if (this.order.length > MAX_TRACKED) {
      for (const dropped of this.order.splice(MAX_TRACKED)) { this.byRequest.delete(dropped); this.touchedAtVersion.delete(dropped) }
    }
    this.rebuild()
  }

  /** Record a submission before any server answer, so the row appears at once. */
  submitting(
    clientRequestId: string,
    label: string,
    at: number,
    broker: OrderBroker | null = null,
    premiumLimit: number | null = null,
  ): void {
    this.put({
      clientRequestId,
      premiumLimit,
      premiumWaiting: premiumLimit !== null,
      intentId: null,
      state: 'SUBMITTING',
      refusedGate: null,
      message: null,
      legs: [],
      at,
      label,
      broker,
    })
  }

  /** The server's ack. A rejected ack is terminal; the engine never saw it. */
  acked(clientRequestId: string, ok: boolean, intentId: string | null, reason?: string): void {
    const existing = this.byRequest.get(clientRequestId)
    if (!existing) return
    if (!ok && existing.intentId !== null && existing.state !== 'SUBMITTING' && existing.state !== 'CREATED') return
    this.put({
      ...existing,
      intentId: intentId ?? existing.intentId,
      state: ok ? (existing.state === 'SUBMITTING' ? 'CREATED' : existing.state) : 'REFUSED',
      message: reason ?? existing.message,
    })
  }

  /** A missing HTTP response is an unknown outcome, never proof of refusal. */
  uncertain(clientRequestId: string, message: string): void {
    const existing = this.byRequest.get(clientRequestId)
    if (existing && existing.intentId === null) this.put({ ...existing, state: 'AMBIGUOUS', message })
  }

  /** A delta from the engine. */
  applyEvent(event: ExecutionEvent): void {
    const existing = this.byRequest.get(event.clientRequestId)
    this.put({
      clientRequestId: event.clientRequestId,
      intentId: event.intentId,
      state: event.state,
      premiumLimit: event.premiumLimit ?? existing?.premiumLimit ?? null,
      premiumWaiting: event.premiumWaiting ?? (event.state === 'CREATED' && existing?.premiumWaiting === true),
      refusedGate: event.refusedGate ?? null,
      message: event.message ?? existing?.message ?? null,
      legs: event.legs ?? [],
      at: event.at,
      // An event can arrive for an order this tab did not place — another
      // tab, or the guardian. It still belongs on screen.
      label: existing?.label ?? describe(event),
      // The event does not carry it; whatever we already knew survives.
      broker: existing?.broker ?? null,
    })
  }

  private put(next: TrackedIntent): void {
    if (!this.byRequest.has(next.clientRequestId)) {
      this.order.unshift(next.clientRequestId)
      if (this.order.length > MAX_TRACKED) {
        const dropped = this.order.pop()
        if (dropped) { this.byRequest.delete(dropped); this.touchedAtVersion.delete(dropped) }
      }
    }
    this.byRequest.set(next.clientRequestId, next)
    this.touchedAtVersion.set(next.clientRequestId, this.version + 1)
    this.rebuild()
  }

  private rebuild(): void {
    this.snapshot = this.order
      .map((id) => this.byRequest.get(id))
      .filter((v): v is TrackedIntent => v !== undefined)
    this.version += 1
    for (const fn of this.listeners) fn()
  }

  /** Test seam. */
  reset(): void {
    this.order = []
    this.byRequest = new Map()
    this.touchedAtVersion.clear()
    this.snapshot = []
    this.version += 1
    for (const fn of this.listeners) fn()
  }
}

const describe = (event: ExecutionEvent): string => {
  const first = event.legs?.[0]
  if (!first) return 'Order'
  const more = (event.legs?.length ?? 0) > 1 ? ` +${event.legs.length - 1}` : ''
  return `${first.side} ${first.lots} lot${first.lots === 1 ? '' : 's'}${more}`
}

export const orderStore = new OrderStore()

/** States from which nothing further will happen on its own. */
export const isSettled = (state: TrackedIntent['state']): boolean =>
  state === 'FILLED' || state === 'CANCELLED' || state === 'REJECTED' || state === 'REFUSED'

/** Is this order possibly live at the exchange right now? */
export const isWorking = (state: TrackedIntent['state']): boolean =>
  state === 'SENDING' ||
  state === 'SENT' ||
  state === 'ACKED' ||
  state === 'PARTIAL' ||
  state === 'AMBIGUOUS'
